/**
 * S-27 game-feature toggles — the client-side mirror of the user_settings
 * booleans added by migration 0002 (+ takeover_offers precedent).
 *
 * Shared store (lib/store.ts) so the deck / roulette / berserker screens and
 * the settings screen see ONE truth. Everything defaults TRUE (the server
 * default): affordances never flicker off while the fetch is in flight, and
 * the server enforces regardless — hiding here is pure UX courtesy.
 */
import { useEffect } from "react";
import { api } from "../../lib/api.js";
import { createStore, useStore } from "../../lib/store.js";

export interface FeatureToggleState {
  super_reject_enabled: boolean;
  berserker_enabled: boolean;
  killstreak_enabled: boolean;
  roulette_enabled: boolean;
  takeover_offers: boolean;
  read_receipts: boolean;
  presence_visible: boolean;
  /** true once GET /settings answered at least once this session. */
  loaded: boolean;
}

export const FEATURE_TOGGLE_DEFAULTS: Omit<FeatureToggleState, "loaded"> = {
  super_reject_enabled: true,
  berserker_enabled: true,
  killstreak_enabled: true,
  roulette_enabled: true,
  takeover_offers: true,
  read_receipts: true,
  presence_visible: true,
};

export const featureToggleStore = createStore<FeatureToggleState>({
  ...FEATURE_TOGGLE_DEFAULTS,
  loaded: false,
});

let inflight: Promise<void> | null = null;

/** Refresh the store from GET /settings (deduped across concurrent mounts). */
export function refreshFeatureToggles(): Promise<void> {
  inflight ??= (async () => {
    try {
      const s = (await api.get("/settings")) as Partial<Record<keyof FeatureToggleState, boolean>>;
      featureToggleStore.set({
        super_reject_enabled: s.super_reject_enabled ?? true,
        berserker_enabled: s.berserker_enabled ?? true,
        killstreak_enabled: s.killstreak_enabled ?? true,
        roulette_enabled: s.roulette_enabled ?? true,
        takeover_offers: s.takeover_offers ?? true,
        read_receipts: s.read_receipts ?? true,
        presence_visible: s.presence_visible ?? true,
        loaded: true,
      });
    } catch {
      // Unreachable settings ⇒ keep defaults (all ON); the server still enforces.
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Mount-time hook: kicks a refresh once, then follows the shared store. */
export function useFeatureToggles(): FeatureToggleState {
  const state = useStore(featureToggleStore, (s) => s);
  useEffect(() => {
    if (!featureToggleStore.get().loaded) void refreshFeatureToggles();
  }, []);
  return state;
}
